import { ExtendedInfoType } from "components/types";
import { EncounterDetails, IssuesResponseType } from "entities/issues";
import { InfoTypes, SinglePatientInfo } from "entities/patientInformation";
import { useEffect, useMemo, useState } from "react";
import { TileYearWidth } from "utils/constants";
import { VitalsChartData } from "./types";

/**
 * - Gets the maximum widh of the timeline window as well as all the years from  the first visit to the current year
 */

export default function useYearTimeline(
  patientInformations?: SinglePatientInfo[],
  issues?: IssuesResponseType
) {
  const [maxYearWidth, setMaxYearWidth] = useState(0);
  const [years, setYears] = useState<string[]>([]);

  const latestYear = useMemo(() => {
    const date = new Date();
    let year = date.getFullYear();

    issues?.encounters?.forEach((data) => {
      const encounterYear = data?.date?.split("-")[0];
      if (encounterYear) {
        year = Math.max(year, +encounterYear);
      }
    });

    return year + 1;
  }, [issues?.encounters]);

  useEffect(() => {
    let yearWidth = 0;

    let minEncounterYear = latestYear;

    patientInformations?.forEach((data) => {
      const encounterYear = +(data?.begdate.split("-")[0] || latestYear);
      minEncounterYear = Math.min(minEncounterYear, encounterYear);
      yearWidth = Math.max(
        yearWidth,
        (+latestYear - +encounterYear) * TileYearWidth
      );
    });
    setMaxYearWidth(yearWidth);

    const yearArr: string[] = [];

    for (let index = 0; index <= latestYear - minEncounterYear; index++) {
      yearArr.push((minEncounterYear + index).toString());
    }
    setYears(yearArr.sort());
  }, [patientInformations, latestYear]);

  const groupPatientData = useMemo(() => {
    const groupedData: Record<InfoTypes, ExtendedInfoType[]> = {
      allergy: [],
      dental: [],
      medical_problem: [],
      medication: [],
      surgery: [],
      tests: [],
      vitals: [],
      medical_device: [],
    };
    const date = new Date();

    if (patientInformations) {
      patientInformations.forEach((info) => {
        const beginingDate = +info?.begdate.split("-")[0];
        let endDate = info.enddate ? +info.enddate.split("-")[0] : latestYear;
        if (
          info.type === "surgery" ||
          info.type === "allergy" ||
          info.type === "medical_device" ||
          info.type === "dental"
        ) {
          endDate = latestYear;
        }

        const yearArr = Array.from(
          { length: endDate + 1 - beginingDate },
          (_, index) => (beginingDate + index).toString()
        );

        groupedData[info.type].push({
          ...info,
          contigiousDates: yearArr,
          enddate: info.enddate ?? date.toISOString(),
          tileWidth: (endDate - beginingDate) * TileYearWidth,
          end: endDate.toString(),
          start: beginingDate.toString(),
        });
      });
    }

    return groupedData;
  }, [patientInformations, latestYear]);

  const yearEncounters = useMemo(() => {
    const encounters: Record<string, EncounterDetails[]> = {};
    const vitalChartData: VitalsChartData = {};
    years.forEach((year) => {
      encounters[year.toString()] = [];
      vitalChartData[year.toString()] = [];
    });
    if (issues) {
      issues.encounters.forEach((encounter) => {
        const encounterYear = encounter.date.split("-")[0];
        if (encounterYear !== "0000") {
          if (encounters[encounterYear]) {
            encounters[encounterYear] = [
              ...encounters[encounterYear],
              ...encounter.encounters,
            ];
          } else {
            encounters[encounterYear] = [...encounter.encounters];
          }

          encounter.encounters.forEach((data) => {
            data.vitals.forEach((vitals) => {
              if (vitalChartData[encounterYear]) {
                vitalChartData[encounterYear].push({
                  bps: vitals.bps,
                  bpd: vitals.bpd,
                  pulse: vitals.pulse,
                  temperature: vitals.temperature,
                  height: vitals.height,
                  respiratoryRate: vitals.respiration,
                  weight: vitals.weight,
                  bmi: vitals.BMI,
                  date: vitals.date,
                });
              } else {
                vitalChartData[encounterYear] = [
                  {
                    bps: vitals.bps,
                    bpd: vitals.bpd,
                    pulse: vitals.pulse,
                    temperature: vitals.temperature,
                    height: vitals.height,
                    respiratoryRate: vitals.respiration,
                    weight: vitals.weight,
                    bmi: vitals.BMI,
                    date: vitals.date,
                  },
                ];
              }
            });
          });
        }
      });
    }
    return { encounters, vitalChartData };
  }, [years, issues]);

  return {
    maxYearWidth,
    years,
    yearGroupPatientData: groupPatientData,
    yearEncounters: yearEncounters.encounters,
    vitalsChartData: yearEncounters.vitalChartData,
  };
}
